import { useContext, useMemo } from 'react';
import { ConfiguratorContext } from '@/components/Configurator/ConfiguratorContext';
import { useDisplayContext, useTranslator } from '@canvas-logic/clp-shared-ui';
import { labelId } from '@canvas-logic/clp-localization';

/**
 * Translator for a component slot's title/description. Per ADR-0045 v5
 * canonicals are `cpt.{cptKey}.cmp.{cmpKey}.slot.{title|desc}` — slots only
 * exist on composite product types, so the prefix is fixed.
 */
export interface SlotLabelTranslator {
  title(systemValue: string): string;
  desc(systemValue: string): string;
}

const identitySlotLabel: SlotLabelTranslator = {
  title: (systemValue) => systemValue,
  desc: (systemValue) => systemValue,
};

type CompositeRootResolver = {
  getProduct?: () => { productType?: { productTypeKey?: string } } | undefined;
};

/**
 * Resolve the slot title/description label IDs for the given component key.
 * Falls back to the system value when no override matches; degrades to
 * identity in basic-product mode (slots don't apply) or when the SDK has
 * not yet loaded the composite product.
 *
 * For nested composite slots, the canonical CPT segment must come from the
 * active subtree composite context (`DisplayContext.cptKey`) — which
 * `useSlotDisplayContext` rewrites to the filler CPT — rather than from the
 * root composite product type. Otherwise nested slot translations would be
 * probed under the root CPT prefix and never resolve.
 */
export function useSlotLabel(componentKey: string): SlotLabelTranslator {
  const ctx = useContext(ConfiguratorContext);
  const api = ctx?.sdk?.api as CompositeRootResolver | undefined;
  const displayCtx = useDisplayContext();
  const rootCptKey = api?.getProduct?.()?.productType?.productTypeKey;
  const activeCptKey = displayCtx.cptKey ?? rootCptKey;
  const translator = useTranslator();

  return useMemo<SlotLabelTranslator>(() => {
    if (!activeCptKey) return identitySlotLabel;
    return {
      title: (systemValue) => translator.entity(labelId.slotTitle(activeCptKey, componentKey), displayCtx, systemValue),
      desc: (systemValue) => translator.entity(labelId.slotDesc(activeCptKey, componentKey), displayCtx, systemValue),
    };
  }, [activeCptKey, componentKey, translator, displayCtx]);
}
